import {
    isSameDay,
    addMonths,
    stripToWeek,
    addDays,
    stripToMonth,
    allDaysDisabledBefore,
    allDaysDisabledAfter,
} from "../utils/date";
import * as classNames from "classNames";
import * as React from "react";
import { Div } from "../Html/Div";
import { Month } from "./Month";
import { YearDropdown } from "./YearDropdown";
import Content from "./Resources/ContentContent";
import { SVGicon, svgIcons } from "../Html/SVGicon";
import { JsonDateParser } from "../Utils/JsonDateParser";
import { PublicHolidaysAction } from "../../applications/Dashboard/Components/Common/PublicHolidays/Actions/PublicHolidays";
import { PublicHoliday } from "../../Wrap/Models/Domains/BusinessDays/PublicHoliday";
import { IJsonDate } from "../../Wrap/System/IJsonDate";
import { DateHelper } from "../Helpers/DateHelper";

export interface ICalendarProps extends React.Props<Calendar> {
    dateFormat: string;
    endDate?: Date;
    excludeDates?: Date[];
    excludeWeekends?: boolean;
    businessDaysOnly?: boolean;
    filterDate?: (d: Date) => boolean;
    // locale?: string,
    maxDate?: Date;
    minDate?: Date;
    onClickOutside: (event: any) => void;
    onSelect: (date: Date) => void;
    selected?: Date;
    showYearDropdown?: boolean;
    startDate?: Date;
    todayButton?: string;
}

export interface ICalendarState {
    date: Date;
    businessDaysOnly?: boolean;
    publicHolidays?: Date[];
}

export class Calendar extends React.Component<ICalendarProps, ICalendarState> {
    constructor(props: ICalendarProps) {
        super(props);
        this.state = { date: this.localizeMoment(this.getDateInView()) };
    }

    public componentWillReceiveProps(nextProps: ICalendarProps): void {
        if (nextProps.selected && !isSameDay(nextProps.selected, this.props.selected)) {
            this.setState({
                date: this.localizeMoment(nextProps.selected),
            });
        }
    }

    public componentDidMount(): void {
        if (this.props.businessDaysOnly) {
            this.setNewDate(this.state.date);
        }
    }

    /**
     * sets a new calendar date, and if business days must be excluded fetches public holidays
     * @param newDate 
     * @param checkIfYearChnaged - flag to determin if we should check for the year change (e.g. implicit swith of year on month switching). Helps to prevent unnecessary renderings
     */
    private setNewDate(newDate: Date, checkIfYearChnaged: boolean = false) {
        if (this.props.businessDaysOnly && (!checkIfYearChnaged || this.state.date.getFullYear() !== newDate.getFullYear())) {
            PublicHolidaysAction.getPublicHolidaysByYear(
                newDate.getFullYear(),
                (publicHolidays: PublicHoliday[]) => {
                    const holidayDates: Date[] = publicHolidays?.map((holiday: PublicHoliday) => JsonDateParser.toDate(holiday.HolidayDate)) ?? [];
                    this.setState( {date: newDate, publicHolidays: holidayDates } );
                }
            );
        } else {
            this.setState( {date: newDate } );
        }
    }

    private setNewDateAndFetch

    public render(): JSX.Element {
        const excludeDates: Date[] = [...(this.props.excludeDates || []), ...(this.state.publicHolidays || [])];
        
        return (
            <Div className="datepicker-dropdown" onClickOutside={(event: any) => this.props.onClickOutside(event)}>
                <div className="triangle" />

                <div className="header">
                    {this.renderYearDropdown()}
                    <div className="header-row month">
                        {this.renderPreviousMonthButton()}
                        {this.renderCurrentMonth()}
                        {this.renderNextMonthButton()}
                    </div>
                </div>

                <table className="calendar">
                    <caption className="sr-only">{Globalize.format(this.state.date, this.props.dateFormat)}</caption>
                    <thead className="day-labels">
                        <tr>{this.header()}</tr>
                    </thead>
                    <Month
                        day={this.state.date}
                        onDayClick={(d: Date) => this.handleDayClick(d)}
                        minDate={this.props.minDate}
                        maxDate={this.props.maxDate}
                        excludeDates={excludeDates}
                        excludeWeekends={this.props.businessDaysOnly || this.props.excludeWeekends}
                        filterDate={this.props.filterDate}
                        selected={this.props.selected}
                        startDate={this.props.startDate}
                        endDate={this.props.endDate}
                    />
                </table>

                {this.renderTodayButton()}
            </Div>
        );
    }

    private getDateInView(): Date {
        const selected = this.props.selected;
        const minDate = this.props.minDate;
        const maxDate = this.props.maxDate;

        const current = new Date();
        if (selected) {
            return selected;
        } else if (minDate && minDate.getTime() >= current.getTime()) {
            return minDate;
        } else if (maxDate && maxDate.getTime() <= current.getTime()) {
            return maxDate;
        } else {
            return current;
        }
    }

    // TODO ???
    private localizeMoment(date: Date): Date {
        const d = new Date(date.getTime());
        return d;
    }

    private increaseMonth(): void {
        const newDate = addMonths(this.state.date, 1, true);
        this.setNewDate(newDate, true);
    }

    private decreaseMonth(): void {
        const newDate = addMonths(this.state.date, -1, true);
        this.setNewDate(newDate, true);
    }

    private handleDayClick(day: Date): void {
        this.props.onSelect(day);
    }

    private changeYear(year: number): void {
        const newDate = this.state.date;
        newDate.setFullYear(year);
        this.setNewDate(newDate, true);
    }
 
    private header(): JSX.Element[] {
        const startOfWeek = stripToWeek(this.state.date);
        return [0, 1, 2, 3, 4, 5, 6].map((offset: number) => {
            const day = addDays(startOfWeek, offset);
            return (
                <th key={offset} className="cell-day" scope="col">
                    {Globalize.culture().calendar.days.namesShort[day.getDay()]}
                </th>
            );
        });
    }

    private renderPreviousMonthButton(): JSX.Element {
        const dayBeforeCurrentMonth = addDays(stripToMonth(this.state.date), -1);
        if (allDaysDisabledBefore(dayBeforeCurrentMonth, this.props.minDate)) {
            return null;
        }
        return (
            <button
                type="button"
                className="nav-arrow previous"
                onClick={() => this.decreaseMonth()}
                aria-label="previous month"
            ></button>
        );
    }

    private renderNextMonthButton(): JSX.Element {
        const dayAfterCurrentMonth = addMonths(stripToMonth(this.state.date), 1, true);
        if (allDaysDisabledAfter(dayAfterCurrentMonth, this.props.maxDate)) {
            return null;
        }
        return (
            <button
                type="button"
                className="nav-arrow next"
                onClick={() => this.increaseMonth()}
                aria-label="next month"
            ></button>
        );
    }

    private renderCurrentMonth(): JSX.Element {
        const classes = classNames("current-month", {
            "current-month-hasYearDropdown": this.props.showYearDropdown,
        } as any);

        return (
            <div className={classes}>
                <span className="sr-only">{Content.Current_month}</span>
                {this.getFormattedMonth()}
            </div>
        );
    }

    private getFormattedMonth(): string {
        return this.props.showYearDropdown
            ? Globalize.format(this.state.date, "MMMM")
            : Globalize.format(this.state.date, this.props.dateFormat);
    }

    private renderYearDropdown(): JSX.Element {
        if (!this.props.showYearDropdown) {
            return null;
        }
        const currentYear = this.state.date.getFullYear();
        return (
            <YearDropdown
                onChange={(y: number) => this.changeYear(y)}
                year={this.state.date.getFullYear()}
                minYear={this.props.minDate ? Math.min(this.props.minDate.getFullYear(), currentYear) : null}
                maxYear={this.props.maxDate ? Math.max(this.props.maxDate.getFullYear(), currentYear) : null}
            />
        );
    }

    private renderTodayButton(): JSX.Element {
        if (!this.props.todayButton) {
            return null;
        }
        let now = new Date();
        const nowWithoutMin = `${now.getFullYear()}/${now.getMonth() + 1}/${now.getDate()}`;
        now = new Date(nowWithoutMin);
        return (
            <div className="today-button">
                <button type="button" className="btn secondary" onClick={() => this.props.onSelect(now)}>
                    {this.props.todayButton}
                </button>
            </div>
        );
    }
}
